import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useMyMonitoringGetControlSelectListWithState } from '@apps-common/apis/my-monitoring/control/get-control-select-list-with-state'
import {
  useMyMonitoringCreateUnitTrigger,
  useMyMonitoringGetUnitTrigger,
  useMyMonitoringUpdateUnitTrigger
} from '@apps-common/apis/my-monitoring/triggers'
import { useGetUnitControlSelectList } from '@apps-common/apis/portal/control'
import { useCreateUnitTrigger, useGetUnitTrigger, useUpdateUnitTrigger } from '@apps-common/apis/portal/trigger'
import { CardContainer, ProductNotSupportedCard } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputInfoSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { CidEventSelectModal } from '@apps-common/components/modals/cid-events/CidEventSelectModal'
import { CID_EVENT_TYPE_EVENT, TRIGGER_ALARM_EVENT_TYPES, TRIGGER_CID_EVENT_TYPES } from '@apps-common/config/cid-event-codes'
import { CONTROL_COMMAND_SELECT_OPTIONS, CONTROL_STATE_ON, CONTROL_TRIGGER_TYPE_LATCH } from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { CONTROL_TRIGGER_SCHEDULE_ALLOWED_PRODUCTS } from '@apps-common/config/product-codes'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { TRIGGER_TYPE_ARM_DISARM, TRIGGER_TYPE_EVENT, UNIT_TRIGGER_ACTION_DELAYS, UNIT_TRIGGER_TYPES } from '@apps-common/config/triggers'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { ICidEventModel } from '@apps-common/types/cid-event-code'
import { IUnitTriggerModel } from '@apps-common/types/portal/unit-trigger'
import classNames from 'classnames'

export const FORM_TRIGGER_MODIFY = 'trigger-modify-form'

const buildDefaultValues = (unitId: string): any => ({
  unitTriggerId: 0,
  unitTriggerType: TRIGGER_TYPE_EVENT.value,
  unitId,
  unitControlId: undefined,
  triggerName: '',
  controlName: '',
  desiredState: CONTROL_STATE_ON,
  changeStateOnReverse: false,
  notificationOnTriggered: true,
  actionDelay: 0,
  active: true,
  eventType: CID_EVENT_TYPE_EVENT.value,
  eventCode: undefined,
  partitionCheck: false,
  partition: '',
  zoneCheck: false,
  zone: '',
  userId: 0,
  userName: '',
  dateEdited: new Date().toISOString()
})

interface IProps {
  unitId: string
  productId: string
  triggerId?: number
  onSave: () => void
  onLoading: (value: boolean) => void
  onUpdating: (value: boolean) => void
}

export const TriggerModifyForm: React.FC<IProps> = ({ unitId, productId, triggerId, onSave, onLoading, onUpdating }) => {
  const [event, setEvent] = useState<ICidEventModel | undefined>()

  const btnColorClasses = useProjectHook('bg-brand text-on-brand', 'bg-ctx-mm-brand text-ctx-on-mm-brand')

  const useGetTriggerHook = useProjectHook(useGetUnitTrigger, useMyMonitoringGetUnitTrigger)
  const useControlSelectHook = useProjectHook(useGetUnitControlSelectList, useMyMonitoringGetControlSelectListWithState)
  const useCreateTriggerHook = useProjectHook(useCreateUnitTrigger, useMyMonitoringCreateUnitTrigger)
  const useUpdateTriggerHook = useProjectHook(useUpdateUnitTrigger, useMyMonitoringUpdateUnitTrigger)

  const {
    data: trigger,
    isLoading: triggerIsLoading,
    isRefetching: triggerIsRefetching
  } = useGetTriggerHook({ config: { enabled: triggerId !== undefined && !Number.isNaN(triggerId) }, triggerId: triggerId || 0 })

  const { data: controls, isLoading: controlsIsLoading, isError: controlsIsError } = useControlSelectHook({ config: {}, unitId })

  const { mutate: createMutation, isPending: createIsPending } = useCreateTriggerHook(true, () => {
    onSave()
  })
  const { mutate: updateMutation, isPending: updateIsPending } = useUpdateTriggerHook(true, () => {
    onSave()
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
    setValue,
    reset
  } = useForm<IUnitTriggerModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: buildDefaultValues(unitId)
  })

  useEffect(() => {
    reset(trigger)
  }, [reset, trigger])

  useEffect(() => {
    if (event) {
      setValue('eventCode', event.code)
    }
  }, [setValue, event])

  const selectedTriggerType = watch('unitTriggerType')
  const selectedControlId = watch('unitControlId')

  const onSubmit = (values: IUnitTriggerModel) => {
    if (values.unitTriggerType === TRIGGER_TYPE_ARM_DISARM.value) {
      values.eventCode = ''
      values.zone = ''
      values.zoneCheck = false
    }

    if (values.zone && values.zone.length > 0) {
      values.zoneCheck = true
    } else {
      values.zoneCheck = false
    }

    if (values.partition && values.partition.length > 0) {
      values.partitionCheck = true
    } else {
      values.partitionCheck = false
    }

    if (values.unitTriggerId) {
      updateMutation({ model: values })
    } else {
      createMutation({ model: values })
    }
  }

  const getStateOptions = () => {
    const control = controls?.find((c: any) => c.unitControlId === selectedControlId)

    if (control && !control.requiresControlState && control.triggerType !== CONTROL_TRIGGER_TYPE_LATCH.value) {
      setValue('desiredState', CONTROL_STATE_ON)

      return CONTROL_COMMAND_SELECT_OPTIONS.filter((o) => o.value === CONTROL_STATE_ON)
    }

    return CONTROL_COMMAND_SELECT_OPTIONS
  }

  const isLoading = triggerIsLoading || triggerIsRefetching || controlsIsLoading || controlsIsError
  const isUpdating = isSubmitting || createIsPending || updateIsPending

  useEffect(() => {
    onLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    onUpdating(isUpdating)
  }, [isUpdating])

  return (
    <ProductNotSupportedCard
      bgClasses='bg-surface mx-auto'
      productId={productId}
      message='This product does not support triggers.'
      allowedProductIds={CONTROL_TRIGGER_SCHEDULE_ALLOWED_PRODUCTS}
    >
      <form id={FORM_TRIGGER_MODIFY} noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-4'>
          <CardContainer bgClasses='bg-surface border border-border'>
            <SectionTitle text='Details' classes='ml-[10px] text-text' />
            <VertSpacerLg />
            <div>
              <InputSM
                type='text'
                placeholder='Name to identify this trigger'
                label='Trigger name'
                id='triggerName'
                showLabel
                loading={isLoading}
                disabled={isUpdating}
                registration={register('triggerName', { required: FORM_DEFAULT_REQUIRED })}
                errors={errors.triggerName}
              />
              <SelectInputSM
                type='text'
                placeholder='Event type to trigger on'
                label='Trigger on'
                id='unitTriggerType'
                showLabel
                loading={isLoading}
                disabled={isUpdating}
                defaultValue={TRIGGER_TYPE_EVENT.value}
                registration={register('unitTriggerType', {
                  required: FORM_DEFAULT_REQUIRED
                })}
                options={UNIT_TRIGGER_TYPES}
                errors={errors.unitTriggerType}
              />
              <SelectInputSM
                type='text'
                placeholder='Delay before triggering'
                label='Delay'
                id='actionDelay'
                showLabel
                loading={isLoading}
                disabled={isUpdating}
                defaultValue={0}
                registration={register('actionDelay', {
                  required: FORM_DEFAULT_REQUIRED,
                  valueAsNumber: true
                })}
                options={UNIT_TRIGGER_ACTION_DELAYS}
                errors={errors.actionDelay}
              />
              <VertSpacerMd />
              <CheckboxInputSM
                label='Toggle with event type'
                id='changeStateOnReverse'
                disabled={isLoading || isUpdating}
                registration={register('changeStateOnReverse')}
                errors={errors.changeStateOnReverse}
              />
              <CheckboxInputSM
                label='Notification on triggered'
                id='notificationOnTriggered'
                disabled={isLoading || isUpdating}
                registration={register('notificationOnTriggered')}
                errors={errors.notificationOnTriggered}
              />
              <CheckboxInputSM
                label='Trigger is active'
                id='active'
                disabled={isLoading || isUpdating}
                registration={register('active')}
                errors={errors.active}
              />
            </div>
          </CardContainer>
          <CardContainer bgClasses='bg-surface border border-border'>
            <SectionTitle text='Control' classes='ml-[10px] text-text' />
            <VertSpacerLg />
            <SelectInputSM
              key='unitControlId'
              type='text'
              placeholder='Control to schedule'
              label='Control'
              id='unitControlId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('unitControlId', {
                required: FORM_DEFAULT_REQUIRED,
                valueAsNumber: true
              })}
              options={controls?.map((c: any) => ({ label: c.controlName, value: c.unitControlId })) || []}
              errors={errors.unitControlId}
            />
            <SelectInputSM
              key='desiredState'
              type='text'
              placeholder='desired state of control'
              label='Desired state'
              id='desiredState'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('desiredState', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={getStateOptions()}
              errors={errors.desiredState}
            />
          </CardContainer>
          <AnimatePresence mode='wait'>
            {selectedTriggerType === TRIGGER_TYPE_ARM_DISARM.value && (
              <motion.div key='trigger-arm-disarm-event' {...motionFadeIn(0.25)}>
                <CardContainer bgClasses='bg-surface border border-border'>
                  <SectionTitle text='Arm/Disarm Event' classes='text-text ml-[10px]' />
                  <VertSpacerLg />
                  <SelectInputSM
                    key='eventType'
                    type='text'
                    placeholder='Type of event'
                    label='Arm/Disarm type'
                    id='eventType'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register(`eventType`, {
                      required: FORM_DEFAULT_REQUIRED
                    })}
                    options={TRIGGER_ALARM_EVENT_TYPES}
                    errors={errors?.eventType}
                  />
                  <InputSM
                    key='partition'
                    type='text'
                    placeholder='0 to 99'
                    label='Partition check (If required)'
                    id='partition'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register('partition', {
                      required: false,
                      pattern: {
                        value: /^[0-9]{2}$/,
                        message: '2 numerical characters only e.g. 01'
                      }
                    })}
                    errors={errors.partition}
                  />
                </CardContainer>
              </motion.div>
            )}
            {selectedTriggerType === TRIGGER_TYPE_EVENT.value && (
              <motion.div key='trigger-cid-event' {...motionFadeIn(0.25)}>
                <CardContainer bgClasses='bg-surface border border-border'>
                  <SectionTitle text='CID Event' classes='ml-[10px] text-text' />
                  <VertSpacerLg />
                  <ModalButton
                    classes='w-full text-left flex items-center justify-between gap-[10px]'
                    component={CidEventSelectModal}
                    disabled={isLoading || isUpdating}
                    confirmCallback={(d: ICidEventModel) => setEvent(d)}
                  >
                    <InputSM
                      type='text'
                      placeholder='Trigger on event code'
                      label='Event code'
                      id='eventCode'
                      showLabel
                      containerClassName='grow'
                      className='pointer-events-none'
                      loading={isLoading}
                      disabled={isUpdating}
                      registration={register('eventCode', {
                        required: FORM_DEFAULT_REQUIRED,
                        pattern: {
                          value: /^[0-9]{3}$/,
                          message: '3 numerical characters only e.g. 001'
                        }
                      })}
                      errors={errors.eventCode}
                    />
                    <ButtonRoundSm type='div' className={classNames('mt-[10px]', btnColorClasses)}>
                      <commonIcons.add />
                    </ButtonRoundSm>
                  </ModalButton>
                  <ModalButton
                    classes='w-full text-left'
                    component={CidEventSelectModal}
                    disabled={isLoading || isUpdating}
                    confirmCallback={(d: ICidEventModel) => setEvent(d)}
                  >
                    <InputInfoSM
                      id='cid-event-description'
                      label='Event description'
                      placeholder='Trigger event description'
                      showLabel
                      loading={isLoading}
                      value={event?.description}
                    />
                  </ModalButton>
                  <SelectInputSM
                    key='eventType'
                    type='text'
                    placeholder='Type of event'
                    label='Event type'
                    id='eventType'
                    showLabel
                    info='Whether the trigger will activate on an event or its restoration'
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register(`eventType`, {
                      required: FORM_DEFAULT_REQUIRED
                    })}
                    options={TRIGGER_CID_EVENT_TYPES}
                    errors={errors?.eventType}
                  />
                  <InputSM
                    key='zone'
                    type='text'
                    placeholder='1 to 999'
                    label='Zone/User check (If required)'
                    id='zone'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register('zone', {
                      required: false,
                      pattern: {
                        value: /^[0-9]{3}$/,
                        message: '3 numerical characters only e.g. 001'
                      }
                    })}
                    errors={errors.zone}
                  />
                  <InputSM
                    key='partition'
                    type='text'
                    placeholder='0 to 99'
                    label='Partition check (If required)'
                    id='partition'
                    showLabel
                    loading={isLoading}
                    disabled={isUpdating}
                    registration={register('partition', {
                      required: false,
                      pattern: {
                        value: /^[0-9]{2}$/,
                        message: '2 numerical characters only e.g. 01'
                      }
                    })}
                    errors={errors.partition}
                  />
                </CardContainer>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </form>
    </ProductNotSupportedCard>
  )
}
